import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { HideOnDesktop, HideOnMobile } from '../../../styles/shared';
import {
  TableBody,
  TableBodyRow,
  TableBodyRowCell,
  TableHeader,
} from '../../../components/table/Table';
import {
  FlexContainer,
  ImageRowContainer,
  InfoWithImage,
  LinkWrapper,
  RepeaterWrapper,
  StyledContentWrapper,
  StyledLink,
  StyledMobileLink,
  TableStyled,
  Title,
} from './PastEventsTable.style';
import PaginationArrows from '../../../components/pagination-arrows/PaginationArrows';
import Repeater from '../../../components/repeater/Repeater';
import Link from 'next/link';
import CardEventMobile, {
  CardEventData,
} from '../../../components/cards/card-event-mobile/CardEventMobile';
import CardEvent from '../../../components/cards/card-event/CardEvent';
import { Event } from '../../../models/auto-generated-strapi-types/types/Event';
import { getStrapiMedia } from '../../../utils/media';
import { getPastEvents } from '../../../utils/api';
import { GlobalResponse, Slug } from '../../../models/domain/interfaces';
import { normalizeAttributesNewsEvents } from '../../../utils/event';
import { stringToISODate, serialize } from 'corporate-utils';
import { fromHumanNumberOfColumnsToRepeaterNumberOfColumns } from '../../../utils/carousels';
import Button, { ButtonStyle } from '../../../components/button/Button';
import { useWindowSize } from 'usehooks-ts';
import { useTheme } from '@mui/material';
import SectionHeader from '../../../components/section-header/SectionHeader';
import { EventCardType } from '../../../models/enum';
import { getCompany } from '../../../utils/company';
import { Companies } from 'corporate-types';
import { DEFAULT_IMAGE } from '../../../models/domain/const';
import ImageWithFallback from '../../../components/image-with-fallback/ImageWithFallback';

interface SliderPastEventsProps {
  mainTitle: string;
  mainTitleFontSize?: number;
  mainTitleMobileFontSize?: number;
  mainTitleFontWeight?: number;
  mainTitleMobileFontWeight?: number;
  title: string;
  titleFontSize?: number;
  titleMobileFontSize?: number;
  titleFontWeight?: number;
  titleMobileFontWeight?: number;
  paginationEvents: {
    page: number;
    pageSize: number;
    total: number;
    pageCount: number;
  };
  events: Event[];
}

const INITIAL_MOBILE_ITEM_COUNT = 4;
const INITIAL_ITEM_PER_ROW_XL = 3;
const INITIAL_ITEM_PER_ROW_MD = 2;
const INITIAL_ITEM_PER_ROW_XS = 1;
const INITIAL_ITEMS_ROWS = 2;

const SliderPastEvents: React.FC<any> = ({
  data,
  translations,
  isPrerenderRequest,
}: {
  data: SliderPastEventsProps;
  translations: any;
  isPrerenderRequest: boolean;
}) => {
  const {
    mainTitle,
    mainTitleFontSize,
    mainTitleMobileFontSize,
    mainTitleFontWeight,
    mainTitleMobileFontWeight,
    title,
    titleFontSize,
    titleMobileFontSize,
    titleFontWeight,
    titleMobileFontWeight,
    events,
  } = data ?? {};

  const page = useMemo(
    () => data?.paginationEvents?.page || 1,
    [data?.paginationEvents?.page]
  );
  const pageSize = useMemo(
    () => data?.paginationEvents?.pageSize || 24,
    [data?.paginationEvents?.pageSize]
  );

  const paginationTotalItems = useMemo(
    () => data?.paginationEvents?.total || 0,
    [data?.paginationEvents?.total]
  );

  const [mobileItemsCount, setMobileItemsCount] = useState(
    INITIAL_MOBILE_ITEM_COUNT
  );

  const { width } = useWindowSize();
  const theme = useTheme();

  const [itemsPerRow, setItemsPerRow] = useState<number>(
    INITIAL_ITEM_PER_ROW_XL
  );
  const [itemsRows, setItemsRows] = useState<number>(INITIAL_ITEMS_ROWS);

  useEffect(() => {
    switch (true) {
      case width < theme.breakpointsMediaQuery.values.md &&
        width >= theme.breakpointsMediaQuery.values.sm:
        if (itemsPerRow !== INITIAL_ITEM_PER_ROW_MD) {
          setItemsPerRow(INITIAL_ITEM_PER_ROW_MD);
          setItemsRows(INITIAL_ITEMS_ROWS);
        }
        break;

      case width < theme.breakpointsMediaQuery.values.sm &&
        width >= theme.breakpointsMediaQuery.values.xs:
        if (itemsPerRow !== INITIAL_ITEM_PER_ROW_XS) {
          setItemsPerRow(INITIAL_ITEM_PER_ROW_XS);
          setItemsRows(INITIAL_ITEMS_ROWS);
        }
        break;

      case width >= theme.breakpointsMediaQuery.values.md:
      default:
        if (itemsPerRow !== INITIAL_ITEM_PER_ROW_XL) {
          setItemsPerRow(INITIAL_ITEM_PER_ROW_XL);
          setItemsRows(INITIAL_ITEMS_ROWS);
        }
        break;
    }
  }, [itemsPerRow, theme.breakpointsMediaQuery.values, width]);

  const onChangePage = (page: number): void => {
    router.push(createPagePath(~~page));
  };

  const createPagePath = (pageToChange: number, size?: number): string => {
    const { query, asPath, locale } = router;
    const path = asPath.split('?')[0];
    const queryCopy = {
      ...query,
    };

    delete queryCopy?.slug;

    const newQuery: any = {
      ...queryCopy,
      page: pageToChange.toString(),
    };

    if (size !== undefined && size !== null) {
      newQuery.pageSize = size;
    }

    return `/${locale}${path}?${serialize(newQuery)}`;
  };

  const eventsMapper = (events: Event[]): CardEventData[] =>
    events?.map<CardEventData>((event) => ({
      id: event?.id,
      title: event?.attributes?.title,
      website: event?.attributes?.website,
      fair: event?.attributes?.fair,
      location: event?.attributes?.location,
      startDate: (event?.attributes as any)?.startDate,
      endDate: (event?.attributes as any)?.endDate,
      stand: event?.attributes?.stand,
      slug: event?.attributes?.slug || '',
      labelNation: event?.attributes?.labelNation || '',
      labelParticipation: event?.attributes?.labelParticipation || '',
      logoNation: event?.attributes?.logoNation || undefined,
      logoPartecipation: event?.attributes?.logoPartecipation || undefined,
      url: (event?.attributes as any)?.url,
      isPrerenderRequest,
      image: event?.attributes?.metadata?.shareImage?.data || undefined,
    }));

  const mappedEvents = eventsMapper(events);

  const router = useRouter();
  if (!events?.length) return <></>;

  return getCompany() !== Companies.SIRMAN ? (
    <StyledContentWrapper>
      <HideOnMobile>
        <FlexContainer>
          <Title>{title ?? ''}</Title>
          <PaginationArrows
            index={page - 1}
            totalItems={Math.ceil(paginationTotalItems / pageSize)}
            onLeftArrowClick={() => onChangePage(page - 1)}
            onRightArrowClick={() => onChangePage(page + 1)}
          />
        </FlexContainer>
        <TableStyled>
          <TableHeader
            data={[
              translations?.pages?.newsEvents?.pastEventsSlider?.fair,
              translations?.pages?.newsEvents?.pastEventsSlider?.website,
              translations?.pages?.newsEvents?.pastEventsSlider?.date,
              translations?.pages?.newsEvents?.pastEventsSlider?.stand,
              translations?.pages?.newsEvents?.pastEventsSlider?.participation,
              translations?.pages?.newsEvents?.pastEventsSlider?.nation,
            ]}
          ></TableHeader>
          <TableBody>
            {events &&
              events.map((row, index) => {
                return (
                  <TableBodyRow
                    key={index}
                    onClick={() => router.push((row?.attributes as any)?.url)}
                  >
                    <TableBodyRowCell>
                      {row?.attributes?.fair ?? '-'}
                    </TableBodyRowCell>
                    <TableBodyRowCell>
                      <Link href={row?.attributes?.website || ''} passHref>
                        <StyledLink>
                          {row?.attributes?.website
                            ?.replace?.(/(^\w+:|^)\/\//, '')
                            ?.replace?.(/\/$/, '') ?? '-'}
                        </StyledLink>
                      </Link>
                    </TableBodyRowCell>
                    <TableBodyRowCell>
                      {row?.attributes?.startDate ?? 'N/A'} -{' '}
                      {row?.attributes?.endDate ?? 'N/A'}
                    </TableBodyRowCell>
                    <TableBodyRowCell>
                      {row?.attributes?.stand ?? '-'}
                    </TableBodyRowCell>
                    <TableBodyRowCell>
                      <InfoWithImage>
                        {row?.attributes?.logoPartecipation?.data?.attributes
                          ?.url && (
                          <ImageRowContainer>
                            <ImageWithFallback
                              isPrerenderRequest={isPrerenderRequest}
                              layout="fill"
                              objectFit="contain"
                              src={getStrapiMedia(
                                row?.attributes?.logoPartecipation?.data
                                  ?.attributes?.url ?? ''
                              )}
                              alt={
                                row?.attributes?.logoPartecipation?.data
                                  ?.attributes?.alternativeText || '-'
                              }
                              fallbackSrc={DEFAULT_IMAGE}
                            />
                          </ImageRowContainer>
                        )}

                        {row?.attributes?.labelParticipation
                          ? row?.attributes?.labelParticipation
                          : row?.attributes?.logoPartecipation?.data?.attributes
                            ?.url
                            ? ''
                            : '-'}
                      </InfoWithImage>
                    </TableBodyRowCell>
                    <TableBodyRowCell>
                      <InfoWithImage>
                        {row?.attributes?.logoNation?.data?.attributes?.url && (
                          <ImageRowContainer>
                            <ImageWithFallback
                              isPrerenderRequest={isPrerenderRequest}
                              layout="fill"
                              objectFit="contain"
                              src={getStrapiMedia(
                                row?.attributes?.logoNation?.data?.attributes
                                  ?.url ?? ''
                              )}
                              alt={
                                row?.attributes?.logoNation?.data?.attributes
                                  ?.alternativeText || '-'
                              }
                              fallbackSrc={DEFAULT_IMAGE}
                            />
                          </ImageRowContainer>
                        )}

                        {row?.attributes?.labelNation
                          ? row?.attributes?.labelNation
                          : row?.attributes?.logoNation?.data?.attributes?.url
                            ? ''
                            : '-'}
                      </InfoWithImage>
                    </TableBodyRowCell>
                  </TableBodyRow>
                );
              })}
          </TableBody>
        </TableStyled>
      </HideOnMobile>
      <HideOnDesktop>
        <Title>{title ?? '-'}</Title>
        <RepeaterWrapper>
          <Repeater
            spacing={0}
            cols={{ xs: 12, sm: 6 }}
            props={{ translations, isPrerenderRequest }}
            data={mappedEvents.slice(0, mobileItemsCount)}
            component={CardEventMobile}
          />
          {events.length > mobileItemsCount && (
            <LinkWrapper>
              <StyledMobileLink
                onClick={() =>
                  setMobileItemsCount(
                    mobileItemsCount + INITIAL_MOBILE_ITEM_COUNT
                  )
                }
              >
                {translations?.pages?.newsEvents?.pastEventsSlider?.loadNews}
              </StyledMobileLink>
            </LinkWrapper>
          )}
        </RepeaterWrapper>
      </HideOnDesktop>
    </StyledContentWrapper>
  ) : (
    <StyledContentWrapper>
      <SectionHeader
        subtitle={mainTitle ?? ''}
        mainTitleFontSize={mainTitleFontSize}
        mainTitleMobileFontSize={mainTitleMobileFontSize}
        mainTitleFontWeight={mainTitleFontWeight}
        mainTitleMobileFontWeight={mainTitleMobileFontWeight}
        titleFontSize={titleFontSize}
        title={title ?? ''}
        titleMobileFontSize={titleMobileFontSize}
        titleFontWeight={titleFontWeight}
        titleMobileFontWeight={titleMobileFontWeight}
      />
      <RepeaterWrapper>
        <Repeater
          spacing={3}
          cols={{
            md: fromHumanNumberOfColumnsToRepeaterNumberOfColumns(3),
            sm: fromHumanNumberOfColumnsToRepeaterNumberOfColumns(2),
            xs: fromHumanNumberOfColumnsToRepeaterNumberOfColumns(1),
          }}
          props={{
            translations,
            isPrerenderRequest,
            cardType: EventCardType.SMALL,
          }}
          data={mappedEvents.slice(0, itemsPerRow * itemsRows)}
          component={CardEvent}
        />
        {mappedEvents.length > itemsPerRow * itemsRows ? (
          <LinkWrapper>
            {page > 1 && (
              <Button
                text={
                  translations?.pages?.newsEvents?.pastEventsSlider
                    ?.loadNewerNews
                }
                styleBtn={ButtonStyle?.secondary}
                asA={true}
                onClick={(e) => {
                  e.preventDefault();
                  onChangePage(page - 1);
                }}
                href={createPagePath(page - 1)}
              />
            )}
            <Button
              text={translations?.pages?.newsEvents?.pastEventsSlider?.loadNews}
              styleBtn={ButtonStyle?.secondary}
              onClick={() => setItemsRows(itemsRows + 2)}
            />
          </LinkWrapper>
        ) : (
          <LinkWrapper>
            {page > 1 && (
              <Button
                text={
                  translations?.pages?.newsEvents?.pastEventsSlider
                    ?.loadNewerNews
                }
                styleBtn={ButtonStyle?.secondary}
                asA={true}
                onClick={(e) => {
                  e.preventDefault();
                  onChangePage(page - 1);
                }}
                href={createPagePath(page - 1)}
              />
            )}
            {page * pageSize < data?.paginationEvents?.total && (
              <Button
                asA={true}
                text={
                  translations?.pages?.newsEvents?.pastEventsSlider
                    ?.loadOlderNews
                }
                styleBtn={ButtonStyle?.secondary}
                onClick={(e) => {
                  e.preventDefault();
                  onChangePage(page + 1);
                }}
                href={createPagePath(page + 1)}
              />
            )}
          </LinkWrapper>
        )}
      </RepeaterWrapper>
    </StyledContentWrapper>
  );
};

export const getServerSideProps = async (
  section: any,
  translations: any,
  slugAnalyzed: Slug,
  globalLocale: GlobalResponse
) => {
  const pastEvents = await getPastEvents({
    sort: ['startDate:desc'],
    locale: slugAnalyzed.locale,
    pageSize: slugAnalyzed?.query?.pageSize,
    page: slugAnalyzed?.query?.page,
    datesFormat: translations?.date?.sliderNewsEvents?.format,
  });

  section.events =
    normalizeAttributesNewsEvents(
      pastEvents?.data,
      'events',
      translations,
      globalLocale?.attributes?.newsAndEventsMerged || false,
      slugAnalyzed.locale
    ) || [];

  section.events = section.events
    .sort((a: any, b: any) => {
      const { startDate: startDateA } = a.attributes || {};
      const { startDate: startDateB } = b.attributes || {};

      if (startDateA === startDateB) {
        return 0;
      } else {
        const startDateAParsed = stringToISODate(
          (startDateA as any) ?? '',
          translations?.date?.singleEvent?.format,
          slugAnalyzed?.locale
        ) as string;
        const startDateBParsed = stringToISODate(
          (startDateB as any) ?? '',
          translations?.date?.singleEvent?.format,
          slugAnalyzed?.locale
        ) as string;
        if (
          startDateAParsed &&
          startDateBParsed &&
          new Date(startDateAParsed).getTime() >
            new Date(startDateBParsed).getTime()
        ) {
          return 1;
        } else {
          return -1;
        }
      }
    })
    .reverse();

  section.paginationEvents = pastEvents?.meta?.pagination;
};

export default SliderPastEvents;
